/**
 * DeviceDrawer Component - Device details drawer with history
 * 
 * Features:
 * - Device information display with status and metadata
 * - Device history table showing enable/disable actions with timestamps
 * - Real-time data refresh capabilities
 * - Error handling for API failures and no data scenarios
 * - Sortable table for history data
 * 
 * @author CTS - Ford PM UI Team
 */

import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getDeviceHistoryApi } from '../../api/deviceManagementApi';
import {
  Typography,
  CircularProgress,
  Alert,
  TablePagination
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore
} from '@mui/icons-material';
import DateUtils from '../../utils/DateUtils.jsx';
import {
  DeviceDrawerContainer,
  DeviceDrawerContent,
  DeviceDrawerTableWrapper,
  DeviceDrawerStatusChip,
  DeviceDrawerLoadingMessage,
  DeviceDrawerErrorBox,
  DeviceDrawerTabContentPanel,
  DeviceDrawerTabContentHeader,
  DeviceDrawerSortIcon,
  DeviceDrawerCloseIcon,
  DeviceDrawerLoadingSpinner,
  DeviceDrawerAlertContainer,
  DeviceDrawerEmptyStateCell,
  DeviceDetailRowContainer
} from './DeviceDrawer.styles';

import { PlugDrawerMetaSection, PlugDrawerStatusChip, PlugDrawerHeader, PlugDrawerSecondaryButton } from '../dashBoard/PlugDrawer.styles';
import StatusIndicator from '../../components/StatusIndicator.jsx';
import { DashboardMonospaceText } from '../dashBoard/Dashboard.styles';
import { AppLabels } from '../../constant/constant';
import { renderCriticalDeviceList, formattedText } from '../../utils/TextTooltipUtils.jsx';


// Helper component to render metadata fields - matching OfflineEventsDrawer pattern
const MetadataField = ({ label, value, isChip = false, chipType = null, isMonospace = false }) => (
  <DeviceDetailRowContainer>
    <Typography variant="caption" className="detail-label">
      {label}
    </Typography>
    {isChip ? (
      <StatusIndicator status={value} size="medium" />
    ) : isMonospace ? (
      <DashboardMonospaceText>
        {value || AppLabels.notApplicable}
      </DashboardMonospaceText>
    ) : (
      <Typography variant="body2" className="detail-value">
        {value || AppLabels.notApplicable}
      </Typography>
    )}
  </DeviceDetailRowContainer>
);

MetadataField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isChip: PropTypes.bool,
  chipType: PropTypes.string,
  isMonospace: PropTypes.bool
};

MetadataField.defaultProps = {
  value: null,
  isChip: false,
  chipType: null,
  isMonospace: false
};


/**
 * SortableHeader Component - Clickable table header with sort indicators
 */
const SortableHeader = ({ children, field, sortField, sortDirection, onSort }) => {
  const handleSort = () => {
    if (sortField === field) {
      onSort(field, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(field, 'asc');
    }
  };

  const getSortIcon = () => {
    if (sortField !== field) {
      return <DeviceDrawerSortIcon as={UnfoldMore} className="inactive" />;
    }
    return sortDirection === 'asc'
      ? <DeviceDrawerSortIcon as={ArrowUpward} />
      : <DeviceDrawerSortIcon as={ArrowDownward} />;
  };

  return (
    <th
      onClick={handleSort}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '4px'
      }}>
        {children}
        {getSortIcon()}
      </div>
    </th>
  );
};

SortableHeader.propTypes = {
  children: PropTypes.node.isRequired,
  field: PropTypes.string.isRequired,
  sortField: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSort: PropTypes.func.isRequired
};

SortableHeader.defaultProps = {
  sortField: null
};

// Common device drawer utilities to reduce duplication
const DEVICE_DRAWER_UTILS = {
  // Sorting utilities
  sorting: {
    getDirectionalResult: (comparison, direction) => {
      return direction === 'asc' ? comparison : -comparison;
    },

    handleNullValues: (aVal, bVal, direction) => {
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return DEVICE_DRAWER_UTILS.sorting.getDirectionalResult(1, direction);
      if (bVal == null) return DEVICE_DRAWER_UTILS.sorting.getDirectionalResult(-1, direction);
      return null;
    },

    compareDates: (aVal, bVal, direction) => {
      const aTime = new Date(aVal).getTime();
      const bTime = new Date(bVal).getTime();
      
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) return null;
      if (aTime === bTime) return 0;
      
      const comparison = aTime < bTime ? -1 : 1;
      return DEVICE_DRAWER_UTILS.sorting.getDirectionalResult(comparison, direction);
    },

    compareStrings: (aVal, bVal, direction) => {
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (aStr === bStr) return 0;
      const comparison = aStr < bStr ? -1 : 1;
      return DEVICE_DRAWER_UTILS.sorting.getDirectionalResult(comparison, direction);
    },

    compareValues: (aVal, bVal, direction, isDate = false) => {
      const nullResult = DEVICE_DRAWER_UTILS.sorting.handleNullValues(aVal, bVal, direction);
      if (nullResult !== null) return nullResult;

      if (isDate) {
        const dateResult = DEVICE_DRAWER_UTILS.sorting.compareDates(aVal, bVal, direction);
        if (dateResult !== null) return dateResult;
      }

      return DEVICE_DRAWER_UTILS.sorting.compareStrings(aVal, bVal, direction);
    },

    isDateField: (field) => field.includes('time') || field.includes('date'),

    sortData: (data, field, direction) => {
      return [...data].sort((a, b) => {
        const isDate = DEVICE_DRAWER_UTILS.sorting.isDateField(field);
        return DEVICE_DRAWER_UTILS.sorting.compareValues(a[field], b[field], direction, isDate);
      });
    }
  },

  // Common chip class utility
  getChipClass: (status = '') => {
    const s = status.toLowerCase();
    const statusMap = {
      'chip-ok': ['ok', 'active', 'connected', 'online', 'ready'],
      'chip-warn': ['warn', 'warning', 'degraded'],
      'chip-error': ['error', 'offline', 'failed', 'down']
    };
    
    for (const [chipType, keywords] of Object.entries(statusMap)) {
      if (keywords.some(k => s.includes(k))) return chipType;
    }
    return 'chip-muted';
  }
};

/**
 * DeviceDrawer Component
 */
const DeviceDrawer = ({ device, onClose }) => {
  // State management
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  // Map device data to plugDetails format to match PlugDrawer structure
  const plugDetails = device ? {
    id: device.store_plug_id || device.location_plug_id,
    deviceName: device.sp_DeviceName?.toUpperCase() ?? AppLabels.notApplicable,
    siteId: device.store_name ?? AppLabels.notApplicable,
    locationId: device.location_name ?? AppLabels.notApplicable,
    deviceMac: device.device_mac ?? AppLabels.notApplicable,
    modemId: device.modemId ?? AppLabels.notApplicable,
    status: device.status?.toLowerCase() ?? AppLabels.notApplicable,
    smartPlugLocation: device.smart_plug_location ?? AppLabels.notApplicable,
    criticalDevices: device.criticaldevices || [],
    dualSupplyDevices: device.dualcriticaldevices || [],
  } : null;

  console.log('device', device);

  /**
   * Load device history data
   */
  const loadDeviceHistory = useCallback(async () => {
    if (!device?.device_mac) return;

    setHistoryLoading(true);
    setHistoryError(null);

    try {
      const response = await getDeviceHistoryApi(device.device_mac);
      console.log('Device history response:', response);

      // Handle the response structure - extract history using device MAC as key
      const historyRecords = response?.[device.device_mac] || [];

      // Transform the data to include formatted timestamps and action labels
      const transformedHistory = historyRecords.map((record, index) => ({
        id: index + 1,
        value: record.value,
        comment: record.comment || 'N/A',
        triggeredBy: record.triggeredBy || 'System',
        createdAt: DateUtils.formatDateWithLocalZone(record.createdAt) ?? AppLabels.notApplicable,
      }));

      setHistoryData(transformedHistory);
    } catch (error) {
      console.error('Failed to load device history:', error);
      setHistoryError(error.message || 'Failed to load device history');
    } finally {
      setHistoryLoading(false);
    }
  }, [device?.device_mac]);

  /**
   * Handle refresh button click
   */
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDeviceHistory();
    setRefreshing(false);
  };

  /**
   * Handle sort change
   */
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  /**
   * Handle pagination page change
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handle pagination rows per page change
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  // Use centralized chip class utility
  const chipClass = DEVICE_DRAWER_UTILS.getChipClass;

  // Load history on component mount
  useEffect(() => {
    loadDeviceHistory();
  }, [loadDeviceHistory]);

  // Sort history data
  const sortedHistoryData = DEVICE_DRAWER_UTILS.sorting.sortData(historyData, sortField, sortDirection);

  return (
    <DeviceDrawerContainer
      anchor="right"
      open={true}
      onClose={onClose}
      variant="temporary"
    >
      {/* Header */}
      <PlugDrawerHeader>
        <div>
          <Typography 
            variant="caption" 
            component="div"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 500,
              letterSpacing: '0.5px',
              fontFamily: 'inherit'
            }}
          >
            {AppLabels.deviceManagementLabels.deviceDetailsTitle}
          </Typography>
          <Typography 
            variant="h5" 
            component="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1.5rem',
              lineHeight: 1.2,
              fontFamily: 'inherit'
            }}
          >
            {plugDetails.deviceName}
          </Typography>
        </div>
        <PlugDrawerSecondaryButton onClick={onClose}>
          <DeviceDrawerCloseIcon as={CloseIcon} />
          {AppLabels.plugDrawerLabels.closeDrawer}
        </PlugDrawerSecondaryButton>
      </PlugDrawerHeader>

      {/* Content */}
      <DeviceDrawerContent>

        {plugDetails && (
          <PlugDrawerMetaSection>
            <MetadataField 
              label={AppLabels.plugDrawerLabels.plugIdLabel} 
              value={plugDetails.id} 
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.deviceNameLabel} 
              value={plugDetails.deviceName} 
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.siteLabel} 
              value={plugDetails.siteId} 
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.locationLabel} 
              value={plugDetails.locationId} 
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.deviceMacLabel} 
              value={plugDetails.deviceMac} 
              isMonospace={true}
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.modemIdLabel} 
              value={plugDetails.modemId} 
              isMonospace={true}
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.statusLabel} 
              value={plugDetails.status}
              isChip={true}
              chipType={chipClass(plugDetails.status)}
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.smartPlugLocationLabel} 
              value={formattedText(plugDetails.smartPlugLocation)} 
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.singleSourceDevicesLabel} 
              value={renderCriticalDeviceList(plugDetails.criticalDevices)} 
            />
            <MetadataField 
              label={AppLabels.plugDrawerLabels.dualSourceDevicesDeviceLabel} 
              value={renderCriticalDeviceList(plugDetails.dualSupplyDevices)} 
            />
          </PlugDrawerMetaSection>
        )}


        {/* Device History Section */}
        <DeviceDrawerTabContentPanel>
          <DeviceDrawerTabContentHeader>
            <h4>{AppLabels.deviceManagementLabels.deviceHistoryTitle}</h4>
            <PlugDrawerSecondaryButton
              onClick={handleRefresh}
              disabled={historyLoading || refreshing}
              startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            >
              {refreshing ? AppLabels.deviceManagementLabels.refreshingHistory : AppLabels.deviceManagementLabels.refreshHistory}
            </PlugDrawerSecondaryButton>
          </DeviceDrawerTabContentHeader>

          {historyLoading && (
            <DeviceDrawerLoadingMessage>
              <DeviceDrawerLoadingSpinner as={CircularProgress} size={20} />
              {AppLabels.deviceManagementLabels.loadingDeviceHistory}
            </DeviceDrawerLoadingMessage>
          )}

          {historyError && (
            <DeviceDrawerErrorBox>
              <DeviceDrawerAlertContainer as={Alert} severity="error">
                {AppLabels.deviceManagementLabels.deviceHistoryError} {historyError}
              </DeviceDrawerAlertContainer>
            </DeviceDrawerErrorBox>
          )}

          {!historyLoading && !historyError && (
            <DeviceDrawerTableWrapper>
              <table>
                <thead>
                  <tr>
                    <SortableHeader
                      field="createdAt"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    >
                      {AppLabels.deviceManagementLabels.dateHeader}
                    </SortableHeader>
                    <th>
                      {AppLabels.deviceManagementLabels.actionHeader}
                    </th>
                    <th>
                      {AppLabels.deviceManagementLabels.commentHeader}
                    </th>
                    <th>
                      {AppLabels.deviceManagementLabels.triggeredByHeader}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHistoryData.length === 0 ? (
                    <tr>
                      <DeviceDrawerEmptyStateCell colSpan={4}>
                        {AppLabels.deviceManagementLabels.noDeviceHistoryAvailable}
                      </DeviceDrawerEmptyStateCell>
                    </tr>
                  ) : (
                    sortedHistoryData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((record) => (
                        <tr key={record.id}>
                          <td>{record.createdAt}</td>
                          <td>
                            <DeviceDrawerStatusChip
                              label={record.value === 'enable' ? 'Enable' : 'Disable'}
                              chiptype={record.value === 'enable' ? 'chip-ok' : 'chip-error'}
                              size="small"
                            />
                          </td>
                          <td>{record.comment}</td>
                          <td>{record.triggeredBy}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>

              {/* Table Pagination */}
              {historyData.length > 10 && (
                <TablePagination
                  component="div"
                  count={historyData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}

            </DeviceDrawerTableWrapper>
          )}
        </DeviceDrawerTabContentPanel>

      </DeviceDrawerContent>
    </DeviceDrawerContainer>
  );
};

DeviceDrawer.propTypes = {
  device: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};

export default DeviceDrawer;
